import { cn } from '@global/utils/tailwind'
import { formatInitials, formatRelativeTime } from '../../helpers'
import { t } from '@lingui/core/macro'
import { CircleCheck, CircleX } from 'lucide-react'
import type { QueuedResolutionStatus } from '../../types'
import { Tooltip } from '@components/tooltip/tooltip'

interface Props {
  userName: string
  actionTime: Date
  isModerator?: boolean
  isApproverMode?: boolean
  isPending?: boolean
  onStatusUpdate?: (status: QueuedResolutionStatus) => void
  inReviewMode: boolean
}

export const UserInfo = ({
  userName,
  actionTime,
  isModerator,
  isApproverMode = false,
  isPending = false,
  onStatusUpdate,
  inReviewMode,
}: Props) => (
  <div className="flex items-center justify-between gap-[6px] my-[24px] mb-[8px]">
    <div className="flex items-center gap-[8px]">
      <div
        className={cn(
          'w-[34px] h-[34px] rounded-full bg-purple-600 text-white font-bold text-center leading-[34px]',
          isModerator && 'bg-[#4AA97E]',
        )}
      >
        {formatInitials(userName)}
      </div>
      <div className="flex flex-col gap-[6px]">
        <span className="text-[#30505A] font-semibold leading-none">
          {userName ?? t`Not assigned`}
        </span>
        <span className="text-xs text-[#64748B] leading-none">
          {formatRelativeTime(actionTime)}
        </span>
      </div>
    </div>
    {isApproverMode && isPending && (
      <div className="flex items-center gap-[16px]">
        <Tooltip
          title={t`Approval is unavailable while suggestions are under review by another approver.`}
          className={!inReviewMode ? 'hidden' : ''}
        >
          <CircleCheck
            className={`w-6 h-6 cursor-pointer hover:opacity-80 transition-opacity ${
              inReviewMode ? 'text-gray-400' : 'text-cv-green-600'
            }`}
            onClick={() => onStatusUpdate?.('approved')}
            style={inReviewMode ? { pointerEvents: 'none' } : {}}
          />
        </Tooltip>
        <Tooltip
          title={t`Rejection is unavailable while suggestions are under review by another approver.`}
          className={!inReviewMode ? 'hidden' : ''}
        >
          <CircleX
            className={`w-6 h-6 cursor-pointer hover:opacity-80 transition-opacity ${
              inReviewMode ? 'text-gray-400' : 'text-cv-error-primary'
            }`}
            onClick={() => onStatusUpdate?.('rejected')}
            style={inReviewMode ? { pointerEvents: 'none' } : {}}
          />
        </Tooltip>
      </div>
    )}
  </div>
)
